import EmptyIcon from 'assets/illustration_doc'
import { createArrayOfNumber } from 'common/utils/array'
import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { RecoilState, atom, useRecoilState } from 'recoil'

// material
import BackIcon from '@mui/icons-material/ArrowBackIos'
import RefreshIcon from '@mui/icons-material/Refresh'
import Box from '@mui/material/Box'
import Button, { ButtonOwnProps } from '@mui/material/Button'
import Card, { CardProps } from '@mui/material/Card'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import DateRangePicker, { DateRange } from '../DateRangePicker'

import downloadOutlined from '@iconify/icons-ant-design/download-outlined'
import { Icon, IconifyIcon } from '@iconify/react'
import ThaiDatePicker from '../ThaiDatePicker'

const dummyDate = atom<Date | undefined>({ key: 'dummy-date-state', default: undefined })

interface BaseTableCardProps {
  title: string
  refresh: () => void
  isLoading: boolean
  isEmpty: boolean
  isIconButton?: boolean
  emptyText?: string
  disableRefresh?: boolean
  toolbars?: ReactNode
  disableShowDateFilter?: boolean
  dateRecoilState?: RecoilState<Date | undefined>
  moreFilter?: ReactNode
  download?: () => void
  cardProps?: CardProps
  buttonTitle?: string
  buttonIcon?: IconifyIcon
  buttonColor?: ButtonOwnProps['color']
  onClick?: () => void
  onPrev?: () => void
  titleColor?: string

  enableDateRangeFilter?: boolean
  startDateRecoilState?: RecoilState<Date | undefined>
  endDateRecoilState?: RecoilState<Date | undefined>
  /** NEW for DateRangePicker */
  isDateRangePicker?: boolean
  dateRange?: DateRange
  onDateRangeChange?: (dateRange: DateRange) => void
  minDate?: Date
  maxDate?: Date
  displayValue?: (startDate: Date | null, endDate: Date | null) => string
  onClearDateRange?: () => void
  action?: ReactNode 
}

const BaseTableCard: FC<BaseTableCardProps> = ({
  children,
  title,
  refresh,
  isLoading,
  isEmpty,
  isIconButton = false,
  emptyText = 'no.data',
  toolbars,
  disableRefresh = false,
  disableShowDateFilter = false,
  dateRecoilState,
  moreFilter,
  download,
  cardProps,
  buttonTitle,
  buttonColor = 'primary',
  buttonIcon,
  onClick,
  onPrev,
  titleColor,
  enableDateRangeFilter,
  startDateRecoilState,
  endDateRecoilState,
  isDateRangePicker = false,
  dateRange,
  onDateRangeChange,
  minDate,
  maxDate,
  displayValue,
  onClearDateRange,
  action, 
}) => {
  const { t } = useTranslation('common')
  const theme = useTheme()

  const [date, setDate] = useRecoilState(
    !disableShowDateFilter && dateRecoilState ? dateRecoilState : dummyDate
  )

  const handleChangeDate = (value: Date | null) => {
    if (disableShowDateFilter) return

    if (!value) {
      setDate(undefined)
      return
    }

    setDate(value)
  }

  return (
    <Card sx={{ p: 3 }} {...cardProps}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" sx={{ color: titleColor || theme.palette.text.primary }}>
          {title}
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          {download && (
            <IconButton onClick={download}>
              <Icon icon={downloadOutlined} />
            </IconButton>
          )}
          {toolbars}
          {!disableShowDateFilter && (
            <Stack direction="row" spacing={1} sx={{ position: 'relative' }}>
              {isDateRangePicker && dateRange && onDateRangeChange ? (
                <DateRangePicker
                  value={dateRange}
                  onChange={onDateRangeChange}
                  minDate={minDate}
                  maxDate={maxDate}
                  showLabel={false}
                  displayValue={displayValue}
                  handleClear={onClearDateRange}
                />
              ) : (
                <ThaiDatePicker
                  label={t('search.by.date')}
                  value={date ?? null}
                  onChange={handleChangeDate}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: { size: 'small' },
                    actionBar: { actions: ['clear', 'accept'] },
                  }}
                />
              )}
              {moreFilter}
            </Stack>
          )}
          {!disableRefresh && (
            <IconButton onClick={refresh}>
              <RefreshIcon />
            </IconButton>
          )}

          {onClick && (
            <Button
              variant="contained"
              onClick={onClick}
              sx={{ width: '30vw', minWidth: '100px' }}
            >
              {buttonTitle}
            </Button>
          )}
          {action} 
        </Stack>
      </Stack>
      {isLoading ? (
        <Stack spacing={2} p={2}>
          <Stack direction="row" spacing={2}>
            <Skeleton variant="rectangular" width="100%" />
          </Stack>
          {createArrayOfNumber(10).map((val) => (
            <Stack direction="row" spacing={2} key={val}>
              <Skeleton width="10%" />
              <Skeleton width="30%" />
              <Skeleton width="20%" />
              <Skeleton width="20%" />
              <Skeleton width="20%" />
            </Stack>
          ))}
        </Stack>
      ) : isEmpty ? (
        <Stack justifyContent="center" alignItems="center" spacing={2} height={420}>
          <EmptyIcon width={200} />
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
            {t(emptyText)}
          </Typography>
        </Stack>
      ) : (
        <Box py={2}>{children}</Box>
      )}
    </Card>
  )
}

export default BaseTableCard